import React, {FC, useEffect, useMemo} from 'react';
import {useTranslation} from "react-i18next";
import {
    differenceInBusinessDays,
    getHours,
    isAfter,
    startOfToday
} from 'date-fns'
import {AvailabilityInterface, deliveryOptionsInterface, DeliveryPromo, DeliveryType, DeliveryTypePromoMatrix, FormType, gunOptionsInterface, GunTypePromoMatrix} from "../../types/types";
import {deliveryPriceCalculator, getBookingConfig, showDate} from "../../data/price";
import {DeliveryManager} from "../../data/DeliveryManager";
import { setDeliveryType } from '../../stateReducers/deliverySlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { DPDDeliveryService } from '../../api/DPDDeliveryService';
import { TFDeliveryService } from '../../api/TFDeliveryService';
import { bestDeliveryPromo } from '../../api/PromotionService';
import { deliveryCodeToReadableName, deliveryPromoTypeToCode, enumFindMemberByKey, getIntervalFromActivePromo, gunKeyFromGunType, isBow, isLaser, isTransforceDelivery } from '../utils/stateHelper';

interface DeliveryOptionsProps{
    availability: AvailabilityInterface,
}

/**
 * Use playStart for checking for delivery promotions - no need to check whole interval (deicision by Martijn)
 */
const DeliveryOptions: FC<DeliveryOptionsProps> = ({availability}) => {
        const {t} = useTranslation();
        const d = useAppDispatch();
        const formType = useAppSelector((state) => {
            return state.delivery.formType;
        });
        const deliveryOptions = useAppSelector((state) => {
            return state.delivery;
        });
        const gunOptions = useAppSelector((state) => {
            return state.gunOptions;
        });
        const lang = useAppSelector((state) => {
            return state.globals.lang;
        });
        const activePromo = useAppSelector((state) => {
            return state.delivery.activePromo;
        })

        const bookingConfig = getBookingConfig();
        const manager = new DeliveryManager(bookingConfig);

        useEffect(() => {
            console.log("New availabilityData in DeliveryOptions", availability);
        }, [availability]);

        // Not yet checked for holiday. Checking done in next function.
        const availableDeliveriesForGuns = useMemo(() => {
            console.log("Calculating availableDeliveriesForGuns...", availability);
            if (
                (!availability.transforce && !availability.dpd)
                || !gunOptions.quantity
            ) {
                console.log("Returning empty in availableDeliveriesForGuns", availability);
                return [];
            } else {
                const promoInterval = getIntervalFromActivePromo(activePromo, bookingConfig.promoIntervals);
                let gunKey = gunKeyFromGunType(gunOptions.gunType!, bookingConfig.standardPromotion);
                if (gunKey === undefined) {
                    console.error("gunType not supported in standard promotion. Probably invalid: " + gunOptions.gunType);
                    return [];
                }
                
                // gunKeyFromGunType has already checked that this key exists
                let promotion: DeliveryTypePromoMatrix =
                    bookingConfig.standardPromotion[gunKey]!;
                if (
                    promoInterval !== undefined && promoInterval.promotion !== undefined
                ) {
                    gunKey = gunKeyFromGunType(gunOptions.gunType!, promoInterval.promotion);
                    if (gunKey !== undefined) {
                        // We just confirmed that gunKey exists via gunKeyFromGunType
                        promotion = promoInterval.promotion[gunKey as keyof GunTypePromoMatrix]!;
                        console.log("Investigating delivery options for promo: " + promoInterval.name);   
                    } else {
                        console.log("Investigating standard delivery options", promotion);    
                    }
                } else {
                    console.log("Investigating standard delivery options", promotion);
                }

                const deliveryTypes: DeliveryType[] = [];
                
                // DPD point delivery/return
                // Quantity checking happens in `bestDeliveryPromo`
                if (promotion.dpd?.deliveryPromos) {
                    console.log("Investigating DPD delivery promotions...", promotion.dpd.deliveryPromos);
                    const deliveryPromo = bestDeliveryPromo(
                        promotion.dpd.deliveryPromos,
                        gunOptions.gunType!,
                        gunOptions.quantity
                    );
                    if (deliveryPromo !== null) {
                        console.log("DPD delivery promotion found", deliveryPromo);
                        const code = deliveryPromoTypeToCode(deliveryPromo.deliveryType);
                        if (code) {
                            deliveryTypes.push(code);
                        } else {
                            console.error("deliveryPromoTypeToCode returned null. Type: " + deliveryPromo.deliveryType);
                        }
                    }
                }

                // Transforce delivery DPD return
                if (promotion.transforceDpd?.deliveryPromos) {
                    console.log("Investigating Transforce-DPD delivery promotions", promotion.transforceDpd.deliveryPromos);
                    const deliveryPromo = bestDeliveryPromo(
                        promotion.transforceDpd.deliveryPromos,
                        gunOptions.gunType!,
                        gunOptions.quantity
                    );
                    if (deliveryPromo !== null) {
                        console.log("Transforce-DPD delivery promotion found", deliveryPromo);
                        const code = deliveryPromoTypeToCode(deliveryPromo.deliveryType);
                        if (code) {
                            deliveryTypes.push(code);
                        } else {
                            console.error("deliveryPromoTypeToCode returned null. Type: " + deliveryPromo.deliveryType);
                        }
                    }
                }

                // Transforce
                if (promotion.transforce?.deliveryPromos) {
                    console.log("Investigating Transforce delivery promotions", promotion.transforce.deliveryPromos);
                    const deliveryPromo = bestDeliveryPromo(
                        promotion.transforce.deliveryPromos,
                        gunOptions.gunType!,
                        gunOptions.quantity
                    );
                    if (deliveryPromo !== null) {
                        console.log("Transforce delivery promotion found", deliveryPromo);
                        const code = deliveryPromoTypeToCode(deliveryPromo.deliveryType);
                        if (code) {
                            deliveryTypes.push(code);
                        } else {
                            console.error("deliveryPromoTypeToCode returned null. Type: " + deliveryPromo.deliveryType);
                        }
                    }
                }
                
                return deliveryTypes;
            }
        }, [deliveryOptions.startDate, deliveryOptions.endDate, bookingConfig, availability, availability.transforce, availability.dpd]);

        // Doesn't check for holiday yet
        const availableDeliveriesForDate = useMemo(() => {
            const now = new Date();
            const playStart = deliveryOptions.playStart;
            return availableDeliveriesForGuns.filter((delivery) => {
                if (playStart === null) {
                    return false;
                }
                if (manager.isDPDPointDelivery(delivery)) {
                    if (!availability.dpd) {
                        return false;
                    }
                    const difference = -1 * differenceInBusinessDays(now, playStart);
                    if (getHours(now) < 16) {
                        return difference >= 3;
                    } else {
                        return difference > 3;
                    }
                }
            if (manager.isTransforceDelivery(delivery)) {
                    if (!availability.transforce) {
                        return false;
                    }
                    return differenceInBusinessDays(playStart, now) >= 1;
                }
                return false;
            });
        }, [deliveryOptions.playStart, availableDeliveriesForGuns, availability.transforce, availability.dpd]);

        const availableDeliveriesCheckedForHolidays = useMemo(() => {
            const playStart = deliveryOptions.playStart;
            if (playStart === null) {
                return [];
            }
            return availableDeliveriesForDate.filter((delivery) => {
                if (manager.isDPDPointDelivery(delivery)) {
                    return isAfter(DPDDeliveryService.latestShippingDateBefore(playStart), startOfToday());
                }
                if (manager.isTransforceDelivery(delivery)) {
                    return isAfter(TFDeliveryService.latestShippingDateBefore(playStart), startOfToday());
                }
                return true;
            });
        }, [deliveryOptions.playStart, availableDeliveriesForDate]);

        const availableDeliveriesZippedWithPrices = useMemo(() => {
            return availableDeliveriesCheckedForHolidays.map((delivery) => {
                console.log('Delivery to zip', delivery);
                const deliveryName = deliveryCodeToReadableName(delivery) ?? null;
                if (deliveryName === null) {
                    return {
                        deliveryCode: delivery,
                        price: null
                    }
                } else {
                    console.log(`Delivery readable name: ${deliveryName}`);
                    const deliveryPriceResult = deliveryPriceCalculator(gunOptions, deliveryName);
                    return {
                        deliveryCode: delivery,
                        price: deliveryPriceResult?.price ?? null,
                        originalPrice: deliveryPriceResult?.originalPrice ?? null
                    }
                }
                
            });
        }, [deliveryOptions.playStart, availableDeliveriesForDate]);

        function getDeliveryOptionHtml(deliveryCode, price, originalPrice: number | null) {
            let strikeThroughPrefix = "";
            let content = "";
            if (price === null) {
                if (originalPrice !== null) {
                    strikeThroughPrefix += originalPrice.toString() + " " + t('euro');
                }
                content += t('delivery.' + deliveryCode);
            }
            if (price !== null) {
                if (originalPrice !== null && originalPrice > price) {
                    strikeThroughPrefix += originalPrice.toString() + " " + t('euro');
                }
                content += t('delivery.' + deliveryCode, {price: price});
            }

            return <><s>{strikeThroughPrefix}</s> <span dangerouslySetInnerHTML={{__html: content}}></span></>
        }

        return (
            <div className="flex justify-between flex flex-col lg:flex-row mt-2">
                <div>
                    <legend className="text-base font-normal m-0">{t('delivery.options')}</legend>
                    {availableDeliveriesZippedWithPrices.map(({deliveryCode: delivery, price: price, originalPrice: originalPrice}) => {

                        return <div className="form-check" key={delivery}>
                            <label className="form-check-label inline-block text-gray-800" htmlFor={'radio_' + delivery}>
                                <input
                                    className="form-check-input rounded-full h-4 w-4 border border-red-200 bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer bg-gray-300"
                                    type="radio" value={delivery} name="deliveryType"
                                    checked={deliveryOptions.deliveryType === delivery}
                                    onChange={(e) => d(setDeliveryType(e.currentTarget.value))}
                                    id={'radio_' + delivery}/>
                                    {getDeliveryOptionHtml(delivery, price, originalPrice ?? null)}
                            </label>
                            <br/>
                        </div>
                    })
                    }
                    {availableDeliveriesCheckedForHolidays.length === 0 && <div>
                        {t('delivery.noDeliveryOptions')}
                    </div>}
                </div>
            </div>
        );
    }
;

export default DeliveryOptions;
